import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, FileCode, FileJson, FileText, Folder, FolderOpen } from 'lucide-react';
import { langOf } from './codeHighlight';

export interface TreeFile {
  path: string;
  content: string;
}

interface DirNode {
  name: string;
  path: string;
  dirs: DirNode[];
  files: TreeFile[];
}

function buildTree(files: TreeFile[]): DirNode {
  const root: DirNode = { name: '', path: '', dirs: [], files: [] };
  for (const f of files) {
    const parts = f.path.replace(/^\.\//, '').split('/');
    let node = root;
    for (const part of parts.slice(0, -1)) {
      const path = node.path ? `${node.path}/${part}` : part;
      let next = node.dirs.find((d) => d.name === part);
      if (!next) {
        next = { name: part, path, dirs: [], files: [] };
        node.dirs.push(next);
      }
      node = next;
    }
    node.files.push(f);
  }
  const sort = (n: DirNode) => {
    n.dirs.sort((a, b) => a.name.localeCompare(b.name));
    n.files.sort((a, b) => a.path.localeCompare(b.path));
    n.dirs.forEach(sort);
  };
  sort(root);
  return root;
}

function countFiles(n: DirNode): number {
  return n.files.length + n.dirs.reduce((s, d) => s + countFiles(d), 0);
}

function formatSize(n: number) {
  return n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KB`;
}

function FileIcon({ path }: { path: string }) {
  const lang = langOf(path);
  const cls = 'w-3.5 h-3.5 shrink-0';
  if (lang === 'json') return <FileJson className={`${cls} text-amber-300/80`} />;
  if (lang === 'css') return <FileCode className={`${cls} text-pink-300/80`} />;
  if (lang === 'html') return <FileCode className={`${cls} text-orange-300/80`} />;
  if (lang === 'ts') return <FileCode className={`${cls} text-sky-300/80`} />;
  return <FileText className={`${cls} text-gray-400`} />;
}

interface DeliverablesTreeProps {
  files: TreeFile[];
  onOpen: (path: string) => void;
  selectedPath?: string | null;
}

/** Project file tree (folders first, collapsible) for generated deliverables. */
export function DeliverablesTree({ files, onOpen, selectedPath }: DeliverablesTreeProps) {
  const tree = useMemo(() => buildTree(files), [files]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const renderDir = (node: DirNode, depth: number) => (
    <>
      {node.dirs.map((d) => {
        const isOpen = !collapsed[d.path];
        return (
          <div key={d.path}>
            <button
              type="button"
              onClick={() => setCollapsed((c) => ({ ...c, [d.path]: isOpen }))}
              className="w-full flex items-center gap-1.5 py-1 pr-2 rounded-md text-xs text-gray-300 hover:bg-[#1a1d27]"
              style={{ paddingLeft: 8 + depth * 14 }}
            >
              {isOpen ? <ChevronDown className="w-3 h-3 text-gray-500" /> : <ChevronRight className="w-3 h-3 text-gray-500" />}
              {isOpen ? <FolderOpen className="w-3.5 h-3.5 text-[#8b95c9]" /> : <Folder className="w-3.5 h-3.5 text-[#8b95c9]" />}
              <span className="truncate font-medium">{d.name}</span>
              <span className="ml-auto text-[10px] text-gray-600">{countFiles(d)}</span>
            </button>
            {isOpen && renderDir(d, depth + 1)}
          </div>
        );
      })}
      {node.files.map((f) => {
        const active = f.path === selectedPath;
        return (
          <button
            key={f.path}
            type="button"
            onClick={() => onOpen(f.path)}
            title={f.path}
            className={`w-full flex items-center gap-2 py-1 pr-2 rounded-md text-xs transition-colors ${
              active ? 'bg-primary-500/15 text-white' : 'text-gray-400 hover:bg-[#1a1d27] hover:text-gray-100'
            }`}
            style={{ paddingLeft: 8 + depth * 14 + 16 }}
          >
            <FileIcon path={f.path} />
            <span className="truncate flex-1 text-left font-mono">{f.path.split('/').pop()}</span>
            <span className="text-[10px] text-gray-600 tabular-nums">{formatSize(f.content.length)}</span>
          </button>
        );
      })}
    </>
  );

  if (files.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-6 opacity-60">
        <Folder className="w-10 h-10 text-gray-600 mb-3" />
        <div className="text-xs text-gray-500 font-medium">No files yet</div>
        <div className="text-[11px] text-gray-600 mt-1">Generated files appear here as they arrive.</div>
      </div>
    );
  }
  return <div className="py-1">{renderDir(tree, 0)}</div>;
}
